import { createAsyncThunk } from '@reduxjs/toolkit';

import Thunk from 'src/thunks/index';
import history from 'src/libs/history';
import LocalStorage from 'src/utils/LocalStorage';
import notification from 'src/store/ui/notification';
import app from 'src/store/ui/app';
import { Data } from 'src/interfaces/general';
import { PATH } from 'src/constants/app';

const apiUrl = 'me';

const ivaAuth = new Thunk();
// const ivpAuth = new Thunk(undefined, {
//   baseURL: ENV.API.AUTH_HOST,
// });

export type MePayload = {
  access_token: string;
  refresh_token: string;
  redirect?: string;
  expired_at?: number;
  skip_update_storage?: boolean;
};

export type GoogleLoginPayload = {
  platform: string;
  provider: string;
  token: string;
};

export type UpdateMePayload = {
  name?: string;
  bio?: string;
  linkedin?: string;
  phone?: string;
  email?: string;
  interests?: string[];
  profile_picture?: string;
};

export type ManualLoginPayload = {
  email: string;
  password: string;
  platform: string;
};

export type SendResetPasswordPayload = {
  email: string;
  verification_method: 'OTP' | 'LINK';
  redirect: string;
};

export type ResetPasswordPayload = {
  uid: string;
  code: string;
} & ManualLoginPayload;

export type RegisterPayload = {
  email: string;
  name: string;
  password?: string;
  redirect?: string;
  verification_method?: 'OTP' | 'LINK';
};

export type VerifyAccount = {
  uid: string;
  code: string;
  platform: string;
};

export type ConnectIVAEmail = {
  domain: string;
  provider: string;
  code: string;
  redirect_url: string;
};

export const socialLogin = ivaAuth.create<Data<Model.Auth>, GoogleLoginPayload>(
  'auth/social/authenticate',
  {
    onSuccess: async ({ data, dispatch }) => {
      await dispatch(fetchMe({ ...data.data.token, redirect: '/' }));
    },
  },
);

export const manualLogin = ivaAuth.create<Data<Model.Auth>, ManualLoginPayload>(
  'auth/authenticate',
  {
    onSuccess: async ({ data, dispatch }) => {
      await dispatch(fetchMe({ ...data.data.token, redirect: '/' }));
    },
  },
);

export const sendResetPasswordRequest = ivaAuth.create<
  { message: string; data: { user_id: string } },
  SendResetPasswordPayload
>('auth/account/reset-password/send-email');

export const resetPassword = ivaAuth.create<
  Data<Model.Me>,
  ResetPasswordPayload
>('auth/account/reset-password/verify', {
  async onSuccess({ dispatch, data, args }) {
    await dispatch(
      manualLogin({
        password: args.password,
        email: args.email,
        platform: args.platform,
      }),
    );
  },
});

export const fetchMe = createAsyncThunk<any, MePayload>(
  `get_${apiUrl}`,
  async (
    { access_token, refresh_token, redirect, expired_at, skip_update_storage },
    { dispatch },
  ) => {
    try {
      const res = await ivaAuth.get('me', {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });

      if (skip_update_storage) return res.data.data;
      LocalStorage.saveAuthInfo(res.data.data);
      LocalStorage.setToken({
        access_token,
        refresh_token,
        expired_at,
      });
      dispatch(app.updateMe(res.data.data));
      redirect && history.push(redirect);

      return res.data.data;
    } catch (err: any) {
      // dispatch(
      //   notification.on({ message: err.response.data?.message, type: 'error' }),
      // );
    }
  },
);

export const updateMe = ivaAuth.update<any, UpdateMePayload>('me', {
  onSuccess: async ({ dispatch }) => {
    await dispatch(
      fetchMe({
        access_token: LocalStorage.accessToken!,
        refresh_token: LocalStorage.refreshToken!,
      }),
    );
    dispatch(notification.on({ type: 'success', message: 'Success' }));
  },
});

export const register = ivaAuth.create<Data<Model.Me>, RegisterPayload>(
  'auth/register',
);

export const verify = ivaAuth.create<
  Data<{ user: Model.Me } & Model.Auth>,
  VerifyAccount
>('auth/account/verification/verify', {
  onSuccess: ({ data, dispatch }) => {
    LocalStorage.saveAuthInfo(data.data.user);
    LocalStorage.setToken(data.data.token);
    dispatch(app.updateMe(data.data.user));
    history.push(PATH.LOGIN);
  },
});

export const connectAcademyEmail = ivaAuth.create<
  Data<Model.AcademyAuth>,
  ConnectIVAEmail
>('user-auth', {
  async onSuccess({ dispatch, data }) {
    await dispatch(
      fetchMe({
        access_token: LocalStorage.accessToken!,
        refresh_token: LocalStorage.refreshToken!,
      }),
    );
  },
});

export const firstLogin = ivaAuth.create<void, {}>('first-login', {
  async onSuccess({ dispatch }) {
    await dispatch(
      fetchMe({
        access_token: LocalStorage.accessToken!,
        refresh_token: LocalStorage.refreshToken!,
      }),
    );
  },
});

export const logoutApp = ivaAuth.fetch<void, void>('logout');

export type UpdatePassword = {
  new_password: string;
  current_password: string;
};

export const updatePassword = ivaAuth.update<Data<Model.Me>, UpdatePassword>(
  'users/password',
  {
    onSuccess({ dispatch, data }) {
      dispatch(notification.on({ type: 'success', message: data.message }));
      dispatch(app.updateMe(data.data));
    },
  },
);
